import { useState } from 'react'
import { useAutoSaveForm } from '../../../components/forms/useAutoSaveForm'
import FormHeader from '../../../components/forms/FormHeader'
import { api } from '../../../lib/api'
import { toast } from '../../../lib/toast'
import Input from '../../../components/ui/Input'
import Button from '../../../components/ui/Button'

interface Tweet {
  url: string
  oembed_html: string
  author?: string
  fetched_at: string
}

interface SettingsState {
  display_duration: number
  show: boolean
  rich: boolean
  tracked_handles: string[]
}

interface Props {
  overlayId: string
  initial: Record<string, unknown>
}

const MAX_HANDLES = 10

// Otomatik Twitter Feed: bu overlay'in kendi tracked_handles listesi var.
// Bot sadece bu overlay'in OBS'de açık olduğu zamanlarda filter rule kurar
// → kapalı overlay = sıfır maliyet.
export default function TwitterFeedAutoForm({ overlayId, initial }: Props) {
  const tweets = ((initial.tweets as Tweet[]) || [])
  const [newHandle, setNewHandle] = useState('')

  const settings = useAutoSaveForm<SettingsState>({
    initial: {
      display_duration: Number(initial.display_duration) || 12,
      show: initial.show !== false,
      rich: initial.rich !== false,
      tracked_handles: ((initial.tracked_handles as string[]) || []).map((h) => h.toLowerCase()),
    },
    onSave: async (s) => {
      await api(`/api/overlays/${overlayId}/state`, {
        method: 'PATCH',
        body: JSON.stringify(s),
      })
    },
  })

  function addHandle(e: React.FormEvent) {
    e.preventDefault()
    const h = newHandle.trim().toLowerCase().replace(/^@/, '')
    if (!h) return
    const cur = settings.draft.tracked_handles
    if (cur.includes(h)) {
      toast.error(`@${h} zaten listede`)
      setNewHandle('')
      return
    }
    if (cur.length >= MAX_HANDLES) {
      toast.error(`Max ${MAX_HANDLES} hesap takip edebilirsin`)
      return
    }
    settings.patch({ tracked_handles: [...cur, h] })
    setNewHandle('')
  }

  function removeHandle(h: string) {
    settings.patch({ tracked_handles: settings.draft.tracked_handles.filter((x) => x !== h) })
  }

  return (
    <div className="space-y-5">
      <FormHeader
        title="Twitter Feed (Otomatik)"
        subtitle="Bu overlay aktif + OBS'de açıkken bot tweet'leri otomatik akıtır"
        status={settings.status}
        savedAt={settings.savedAt}
        error={settings.error}
        onUndo={settings.canUndo ? settings.undo : undefined}
      />

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4 space-y-3">
        <h3 className="text-sm font-semibold">Takip Edilecek Twitter Hesapları</h3>
        <p className="text-xs text-[var(--color-text-muted)]">
          Bu overlay'de göstereceğin Twitter hesapları. OBS browser source bu overlay'i
          açtığı an bot otomatik filter rule kurar — kapatınca kalkar (maliyet sıfır).
          Aynı hesabı birden fazla overlay'de kullansan bile sistem dedupe eder.
        </p>

        {settings.draft.tracked_handles.length === 0 ? (
          <div className="text-xs text-[var(--color-text-muted)]">Henüz hesap yok.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {settings.draft.tracked_handles.map((h) => (
              <div
                key={h}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[var(--color-surface-elevated)] border border-[var(--color-border)] text-xs"
              >
                <span>@{h}</span>
                <button
                  onClick={() => removeHandle(h)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                  title="Listeden çıkar"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={addHandle} className="flex gap-2">
          <Input
            placeholder="@username veya username"
            value={newHandle}
            onChange={(e) => setNewHandle(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" variant="primary" disabled={!newHandle.trim()}>
            Ekle
          </Button>
        </form>
        <div className="text-xs text-[var(--color-text-subtle)]">
          Max {MAX_HANDLES} hesap. @ işareti opsiyonel; küçük harfle kaydedilir.
        </div>
      </section>

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4 space-y-3">
        <h3 className="text-sm font-semibold">Ekran Davranışı</h3>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">
            Tweet ekranda kalma süresi (saniye)
          </label>
          <Input
            type="number"
            min={3}
            max={60}
            value={settings.draft.display_duration}
            onChange={(e) => settings.patch({ display_duration: Number(e.target.value) })}
            className="w-32"
          />
          <p className="text-xs text-[var(--color-text-subtle)] mt-1">
            Yeni tweet geldiğinde overlay'de bu kadar süre kalır, sonra gizlenir.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={settings.draft.show}
            onChange={(e) => settings.patch({ show: e.target.checked })}
            className="accent-[var(--color-accent)]"
          />
          Yayında göster
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={settings.draft.rich}
            onChange={(e) => settings.patch({ rich: e.target.checked })}
            className="accent-[var(--color-accent)]"
          />
          Zengin görünüm (foto/video — Twitter widgets.js, ilk yüklemede ~200ms)
        </label>
      </section>

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4">
        <h3 className="text-sm font-semibold mb-2">Bot tarafından eklenen tweet'ler ({tweets.length})</h3>
        {tweets.length === 0 ? (
          <div className="text-xs text-[var(--color-text-subtle)]">
            Henüz tweet yok. Hesap eklersen + bu overlay OBS'de açıksa, hesabın yeni tweet'leri
            burada listelenir.
          </div>
        ) : (
          <ul className="space-y-1 max-h-80 overflow-y-auto">
            {tweets.map((t, i) => (
              <li key={t.url} className="flex items-center gap-2 p-1.5 border-b last:border-0 border-[var(--color-border)]">
                <span className="text-xs font-mono text-[var(--color-text-subtle)] w-6">#{i}</span>
                <span className="text-xs flex-1 truncate">
                  {t.author && <span className="text-[var(--color-accent-hover)]">@{t.author}</span>}{' '}
                  <span className="text-[var(--color-text-muted)]">{t.url}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
